import { TZDateMini } from '@date-fns/tz';
import {
  useCallback,
  useMemo,
  useRef,
  type RefObject,
  ClipboardEventHandler,
  KeyboardEventHandler as ReactKeyboardEventHandler,
} from 'react';
import { useIntl } from 'react-intl';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { isFullTimeframe } from '../../../core/time/helpers.js';
import { useDateTimeRangePickerStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import {
  captureUndoSnapshot,
  normalizeISOInput,
  parseDateOnlyToISO,
  parseTimeOnlyToISO,
} from '../helpers.js';
import type {
  DateTimeInputRef,
  DateTimeRangePickerProps,
  UndoSnapshot,
} from '../types.js';

/**
 * Restores spinbutton DOM text from a snapshot by driving each handler directly.
 */
const restoreSpinButtons = (
  ref: RefObject<DateTimeInputRef | null>,
  spinButtonTexts: Map<Intl.DateTimeFormatPart['type'], string>,
): void => {
  spinButtonTexts.forEach((text, type) => {
    const handler = ref.current?.spinButtonHandlers.get(type) ?? null;
    if (!handler) {
      return;
    }
    if (text === '') {
      handler.clearValue(true);
    } else if (handler.type === 'number') {
      handler.applyText(text, true);
    } else {
      handler.searchValue(text[0] ?? '', true);
    }
  });
};

/**
 * Restores a single from/to snapshot side (spin buttons or expression input).
 */
const restoreSnapshot = (
  snapshot: UndoSnapshot,
  ref: RefObject<DateTimeInputRef | null>,
): void => {
  if (snapshot.valueType === 'iso8601') {
    restoreSpinButtons(ref, snapshot.spinButtonTexts);
  } else if (ref.current?.expressionInputRef) {
    ref.current.expressionInputRef.textContent = snapshot.expressionText;
  }
};

interface UseDateTimeRangePickerProps
  extends Pick<DateTimeRangePickerProps, 'placeholder' | 'readOnly'> {
  dateTimeFromRef: RefObject<DateTimeInputRef | null>;
  dateTimeToRef: RefObject<DateTimeInputRef | null>;
}

/**
 * A hook that provides the default functionality for the DateTimeRange picker
 * @internal
 */
export const useDateTimeRangePicker = (props: UseDateTimeRangePickerProps) => {
  const { placeholder, readOnly, dateTimeFromRef, dateTimeToRef } = props;
  const { formatMessage } = useIntl();
  const value = useDateTimeRangePickerStore(
    (state) => state.dateTimeRangePickerTimeframe,
  );
  const setValue = useDateTimeRangePickerStore(
    (state) => state.setDateTimeRangePickerTimeframe,
  );

  const pasteUndoSnapshotRef = useRef<{
    from: UndoSnapshot;
    to: UndoSnapshot;
    focusTarget: Element | null;
  } | null>(null);

  const localizedPlaceholder = useMemo(
    () => ({
      from:
        placeholder?.from ??
        formatMessage({
          defaultMessage: 'Start time',
          id: 'gnKOHMjpyzk6hNcF',
          description: 'Start of timeframe',
        }),
      to:
        placeholder?.to ??
        formatMessage({
          defaultMessage: 'End time',
          id: 'sGxf5yRVhyC8Fpo/',
          description: 'End of timeframe',
        }),
    }),
    [formatMessage, placeholder?.from, placeholder?.to],
  );

  // for non null values, put the from/to value as `<TimeValue>/<TimeValue>` into the clipboard
  const onCopyCapture: ClipboardEventHandler = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isFullTimeframe(value)) {
        return;
      }

      try {
        await navigator.clipboard.writeText(
          `${value.from.value}/${value.to.value}`,
        );
      } catch {
        // For now, we silently ignore clipboard write errors.
      }
    },
    [value],
  );

  // clipboard paste handler. apply clipboard content if formatted correctly
  const onPasteCapture: ClipboardEventHandler = useCallback(
    (e) => {
      if (readOnly) {
        return;
      }
      const data = e.clipboardData?.getData('text/plain');
      if (!data) {
        e.preventDefault();
        return;
      }

      if (!dateTimeFromRef.current || !dateTimeToRef.current) {
        return;
      }

      // Capture undo snapshots for any paste operation (range or single-input).
      const focusTarget = document.activeElement;
      const fromValueType =
        (dateTimeFromRef.current?.element?.getAttribute('data-valuetype') as
          | 'expression'
          | 'iso8601') ?? 'expression';
      const toValueType =
        (dateTimeToRef.current?.element?.getAttribute('data-valuetype') as
          | 'expression'
          | 'iso8601') ?? 'expression';
      const fromSnapshot = captureUndoSnapshot(
        dateTimeFromRef.current.spinButtonHandlers,
        dateTimeFromRef.current.expressionInputRef,
        value?.from ?? null,
        fromValueType,
      );
      const toSnapshot = captureUndoSnapshot(
        dateTimeToRef.current.spinButtonHandlers,
        dateTimeToRef.current.expressionInputRef,
        value?.to ?? null,
        toValueType,
      );

      // if the clipboard content is of format `<TimeValue>/<TimeValue>`, apply them.
      const [nextFrom, nextTo] = data.trim().split('/');

      if (nextFrom && nextTo) {
        const relativeDate = TZDateMini.tz(getTimezone()).getTime();
        const parsedFrom =
          parseDateOnlyToISO(nextFrom) ??
          parseTimeOnlyToISO(nextFrom) ??
          normalizeISOInput(nextFrom);
        const parsedTo =
          parseDateOnlyToISO(nextTo, 'endOfDay') ??
          parseTimeOnlyToISO(nextTo) ??
          normalizeISOInput(nextTo);

        // Only apply if both sides are valid parseable values.
        const validFrom = parseTimeAsTimeValue(parsedFrom, relativeDate);
        const validTo = parseTimeAsTimeValue(parsedTo, relativeDate);

        if (validFrom && validTo) {
          pasteUndoSnapshotRef.current = {
            from: fromSnapshot,
            to: toSnapshot,
            focusTarget,
          };
          setValue({ from: validFrom, to: validTo });
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      pasteUndoSnapshotRef.current = {
        from: fromSnapshot,
        to: toSnapshot,
        focusTarget,
      };
    },
    [readOnly, setValue, value, dateTimeFromRef, dateTimeToRef],
  );

  /**
   * Ctrl+Z / Cmd+Z handler for the range picker container.
   * Intercepts when a paste undo snapshot is pending and restores the affected input(s).
   */
  const onUndoPasteKeyDown: ReactKeyboardEventHandler = useCallback(
    (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'z' &&
        pasteUndoSnapshotRef.current
      ) {
        const snapshot = pasteUndoSnapshotRef.current;
        pasteUndoSnapshotRef.current = null;

        event.preventDefault();
        event.stopPropagation();

        restoreSnapshot(snapshot.from, dateTimeFromRef);
        restoreSnapshot(snapshot.to, dateTimeToRef);

        // Update the store with the original values so the hidden inputs
        // and any controlled consumers reflect the undo.
        const fromValue = snapshot.from.value ?? undefined;
        const toValue = snapshot.to.value ?? undefined;
        setValue(
          fromValue || toValue ? { from: fromValue, to: toValue } : null,
        );

        // Restore focus to the element that was focused before the paste
        if (
          snapshot.focusTarget instanceof HTMLElement &&
          snapshot.focusTarget !== document.activeElement
        ) {
          snapshot.focusTarget.focus();
        }
      }
    },
    [dateTimeFromRef, dateTimeToRef, setValue],
  );

  return {
    placeholder: localizedPlaceholder,
    onCopyCapture,
    onPasteCapture,
    onUndoPasteKeyDown,
  };
};
